import { Inject, Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpDownloadProgressEvent, HttpEventType } from '@angular/common/http';
import { PRODUCTS_SERVICE_URL } from 'src/app/injection-tokens';
import { Observable, Subject, Subscription, throwError, timer } from 'rxjs';
import { filter, mergeMap, retryWhen, tap } from 'rxjs/operators';
import { DownloadEvent } from './download-job.model';
import { SSE_HTTP_CLIENT } from 'src/app/general-services/sse-http-client';

const BASE_RECONNECT_MS = 1_000;
const MAX_RECONNECT_MS = 30_000;

@Injectable({
  providedIn: 'root',
})
export class DownloadEventsService {
  events$: Observable<DownloadEvent>;

  private readonly sseUrl: string;
  private eventsSubject = new Subject<DownloadEvent>();
  private sseSubscription: Subscription | null = null;
  private reconnectAttempt = 0;
  private active = false;

  constructor(
    @Inject(PRODUCTS_SERVICE_URL) baseUrl: string,
    private ngZone: NgZone,
    @Inject(SSE_HTTP_CLIENT) private http: HttpClient
  ) {
    this.sseUrl = baseUrl + '/downloads/events';
    this.events$ = this.eventsSubject.asObservable();
  }

  connect(): void {
    if (this.active) {
      return;
    }
    this.active = true;
    this.openStream();
  }

  /**
   * Force a fresh SSE connection. Call before triggering a job so the server
   * is guaranteed to know about this client — even after a server restart where
   * the previous stream may have silently stalled.
   */
  reconnect(): void {
    this.active = true;
    this.closeStream();
    this.openStream();
  }

  disconnect(): void {
    this.active = false;
    this.closeStream();
  }

  private openStream(): void {
    this.closeStream();
    this.reconnectAttempt = 0;

    // Auth is handled automatically: Angular's OAuthModule interceptor adds
    // 'Authorization: Bearer <token>' to all HttpClient requests whose URL
    // matches allowedResourceServerUrls — no manual token handling needed here.
    const stream$ = this.http
      .get(this.sseUrl, {
        headers: { Accept: 'text/event-stream' },
        observe: 'events',
        responseType: 'text',
        reportProgress: true,
      })
      .pipe(
        tap(e => {
          // Reset backoff as soon as the server responds with 200 headers.
          // This way a server that closes the stream after a heartbeat retries at a
          // fixed 1s interval rather than accumulating exponential delay, while a
          // truly unreachable server (no 200 ever) still gets exponential backoff.
          if (e.type === HttpEventType.ResponseHeader) {
            this.reconnectAttempt = 0;
          }
        }),
        filter((e): e is HttpDownloadProgressEvent => e.type === HttpEventType.DownloadProgress),
        this.parseSseLines(),
        retryWhen(errors =>
          errors.pipe(
            mergeMap(() => {
              if (!this.active) {
                return throwError(new Error('disconnected'));
              }
              const delay = Math.min(
                BASE_RECONNECT_MS * Math.pow(2, this.reconnectAttempt),
                MAX_RECONNECT_MS
              );
              this.reconnectAttempt++;
              return timer(delay);
            })
          )
        )
      );

    this.sseSubscription = stream$.subscribe({
      next: event => {
        this.ngZone.run(() => {
          this.eventsSubject.next(event);
        });
      },
      error: () => {
        // retryWhen terminated (either disconnected or unrecoverable error)
      },
    });
  }

  private closeStream(): void {
    this.sseSubscription?.unsubscribe();
    this.sseSubscription = null;
  }

  /**
   * Stateful RxJS operator that parses the cumulative partialText from
   * HttpDownloadProgressEvent into DownloadEvent objects.
   *
   * Angular's fetch backend appends to partialText on every chunk, so each
   * event contains all text received so far. flushedLength tracks how far we
   * have already processed, ensuring only new data is scanned — O(new data).
   *
   * The SSE wire format for each event is:   data: <json>\n\n
   * The operator matches every complete "data: ..." line in the new chunk.
   *
   * When the server closes the stream (observable completes), the operator
   * promotes it to an error so retryWhen can schedule a reconnect.
   */
  private parseSseLines() {
    return (source: Observable<HttpDownloadProgressEvent>): Observable<DownloadEvent> =>
      new Observable<DownloadEvent>(observer => {
        let flushedLength = 0;
        return source.subscribe({
          next: event => {
            const cumulative = event.partialText ?? '';
            const chunk = cumulative.slice(flushedLength);
            flushedLength = cumulative.length;
            const regex = /^data: (.+)$/gm;
            let match: RegExpExecArray | null;
            while ((match = regex.exec(chunk)) !== null) {
              try {
                observer.next(JSON.parse(match[1]) as DownloadEvent);
              } catch {
                // malformed JSON in a single event — skip and continue
              }
            }
          },
          error: err => observer.error(err),
          complete: () => observer.error(new Error('SSE stream closed by server')),
        });
      });
  }
}
